import { useRef, useState } from "react";
import { Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type InlineEditableTitleProps = {
  value: string;
  onSubmit: (next: string) => void;
  /** Accessible name for the text input shown while editing. */
  inputAriaLabel: string;
  /** Accessible name for the pencil button that starts editing. */
  editButtonAriaLabel: string;
  /** Classes applied to the read-only text (to match the surrounding heading). */
  className?: string;
  disabled?: boolean;
};

/**
 * Renders a title that can be renamed in place: a pencil button reveals a text
 * input; Enter or blur commits a changed, non-blank value, Escape reverts.
 */
export function InlineEditableTitle({
  value,
  onSubmit,
  inputAriaLabel,
  editButtonAriaLabel,
  className,
  disabled = false,
}: InlineEditableTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // Enter commits and blurs the input, which would otherwise commit again.
  const committed = useRef(false);

  const start = () => {
    committed.current = false;
    setDraft(value);
    setEditing(true);
  };

  const finish = (save: boolean) => {
    if (committed.current) return;
    committed.current = true;
    setEditing(false);
    if (!save) return;
    const next = draft.trim();
    if (next && next !== value) onSubmit(next);
  };

  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        aria-label={inputAriaLabel}
        className="h-7 text-sm"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => finish(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            finish(true);
          } else if (event.key === "Escape") {
            event.preventDefault();
            finish(false);
          }
        }}
      />
    );
  }

  return (
    <span className="group/rename flex min-w-0 items-center gap-1">
      {/* Wraps instead of truncating (ENG-1983): the full title must be
          readable without hover, matching the read-only title rendering on
          the same surfaces. */}
      <span className={cn("min-w-0 break-words", className)}>{value}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/rename:opacity-100"
        onClick={start}
        aria-label={editButtonAriaLabel}
        disabled={disabled}
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
    </span>
  );
}
